/**
 * Two-step cart drawer flow: cart and recommendations, then pickup details.
 */
class CartDrawerPickup {
  constructor(sidebar) {
    this.sidebar = sidebar;
    this.pickupRequired = sidebar.dataset.pickupRequired === 'true';
    this.currentStep = 'items';
    this.scheduler = null;
    this.drawerOpen = sidebar.classList.contains('sidebar--opened');

    this.bindEvents();
    this.showStep('items', { focus: false });
  }

  getScheduler() {
    if ( ! this.scheduler || ! this.sidebar.contains(this.scheduler) ) {
      this.scheduler = this.sidebar.querySelector('[data-cart-pickup-fields] pickup-scheduler');
    }
    return this.scheduler;
  }

  getCheckoutButton() {
    return this.sidebar.querySelector('#CheckOut');
  }

  bindEvents() {
    this.sidebar.addEventListener('click', event => {
      const backButton = event.target.closest('[data-cart-step-back]');
      if ( backButton ) {
        this.showStep('items');
        return;
      }

      const checkoutButton = event.target.closest('#CheckOut');
      if ( checkoutButton && this.currentStep === 'items' ) {
        event.preventDefault();
        this.showStep('pickup');
      }
    });

    this.sidebar.addEventListener('change', event => {
      if ( event.target.matches('#AgreeToTos, [data-cart-contact]') ) {
        this.syncAction();
      }
    });

    this.sidebar.addEventListener('input', event => {
      if ( event.target.matches('[data-cart-contact]') ) {
        this.syncAction();
      }
    });

    this.sidebar.addEventListener('pickupchange', () => this.syncAction());

    this.onStoreChanged = () => {
      this.getScheduler()?.applyStoreConfig?.(false);
      this.syncAction();
    };
    document.addEventListener('storechanged', this.onStoreChanged);

    new MutationObserver(() => {
      const isDrawerOpen = this.sidebar.classList.contains('sidebar--opened');

      if ( isDrawerOpen && ! this.drawerOpen ) {
        this.showStep('items', { focus: false });
      } else if ( ! isDrawerOpen && this.drawerOpen ) {
        this.showStep('items', { focus: false });
      }

      this.drawerOpen = isDrawerOpen;
    }).observe(this.sidebar, { attributes: true, attributeFilter: ['class'] });

    new MutationObserver(mutations => {
      const checkoutReplaced = mutations.some(mutation => (
        [...mutation.addedNodes].some(node => (
          node instanceof Element
          && (node.matches('#CheckOut') || node.querySelector('#CheckOut'))
        ))
      ));

      if ( checkoutReplaced ) {
        this.syncAction();
      }
    }).observe(this.sidebar, { childList: true, subtree: true });

    customElements.whenDefined('pickup-scheduler').then(() => {
      this.getScheduler()?.applyStoreConfig?.(false);
      this.syncAction();
    });
  }

  showStep(step, options = {}) {
    const nextStep = step === 'pickup' ? 'pickup' : 'items';
    const { focus = true } = options;

    this.currentStep = nextStep;
    this.sidebar.dataset.cartStep = nextStep;

    this.sidebar.querySelectorAll('[data-cart-drawer-panel]').forEach(panel => {
      panel.hidden = panel.dataset.cartDrawerPanel !== nextStep;
    });

    this.sidebar.querySelectorAll('[data-cart-step-title]').forEach(title => {
      title.hidden = title.dataset.cartStepTitle !== nextStep;
    });

    const backButton = this.sidebar.querySelector('[data-cart-step-back]');
    if ( backButton ) {
      backButton.hidden = nextStep === 'items';
    }

    if ( nextStep === 'pickup' ) {
      this.getScheduler()?.applyStoreConfig?.(false);
    }

    this.syncAction();

    const visiblePanel = this.sidebar.querySelector(`[data-cart-drawer-panel="${nextStep}"]`);
    visiblePanel?.querySelector('.sidebar__body-top')?.scrollTo({ top: 0, behavior: 'auto' });

    if ( focus ) {
      if ( nextStep === 'pickup' ) {
        this.sidebar.querySelector('[data-cart-step-title="pickup"]')?.focus();
      } else {
        this.getCheckoutButton()?.focus();
      }
    }
  }

  termsAccepted() {
    const termsCheckbox = this.sidebar.querySelector('#AgreeToTos');
    return ! termsCheckbox || termsCheckbox.checked;
  }

  contactComplete() {
    const fields = this.sidebar.querySelectorAll('[data-cart-contact][required]');
    if ( ! fields.length ) {
      return true;
    }
    return [...fields].every(field => field.value.trim() !== '');
  }

  syncAction() {
    const checkoutButton = this.getCheckoutButton();
    if ( ! checkoutButton ) {
      return;
    }

    const nextLabel = checkoutButton.dataset.labelNext || 'Select pickup';
    const checkoutLabel = checkoutButton.dataset.labelCheckout || 'Checkout';

    if ( this.currentStep === 'items' ) {
      checkoutButton.type = 'button';
      checkoutButton.disabled = false;
      checkoutButton.textContent = nextLabel;
      checkoutButton.classList.add('button--solid');
      checkoutButton.classList.remove('button--outline');
      return;
    }

    const complete = this.getScheduler()?.isComplete?.() ?? false;
    const blocked = (this.pickupRequired && ! complete)
      || ! this.termsAccepted()
      || ! this.contactComplete();

    checkoutButton.type = 'submit';
    checkoutButton.disabled = blocked;
    checkoutButton.textContent = checkoutLabel;
    checkoutButton.classList.toggle('button--solid', ! blocked);
    checkoutButton.classList.toggle('button--outline', blocked);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const sidebar = document.getElementById('site-cart-sidebar');
  if ( sidebar?.dataset.pickupEnabled === 'true' ) {
    window.cartDrawerPickup = new CartDrawerPickup(sidebar);
  }
});
